import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import DatePicker from '../ui/DatePicker';
import PhoneInput from '../ui/PhoneInput';
import FieldAnswerList from '../fields/FieldAnswerList';
import { getLead, updateLead, uploadLeadDocument, deleteLeadDocument } from '../../api/leads';
import { listNationalities } from '../../api/masters';
import { listDocumentTypes } from '../../api/workflow';
import { pushToast } from '../ui/toastBus';
import { REASON_LABELS } from './VerifyLeadModal';

const DATE_KEYS = ['date_of_birth', 'passport_expiry_date', 'visa_validity', 'home_license_expiry', 'uae_license_expiry'];
const PHONE_KEYS = ['contact_no', 'whatsapp_no', 'home_country_contact_no'];
const NATIONALITY_FIELD_PREFIX = 'nationality_fields.';

const isNationalityField = (item) => item.key.startsWith(NATIONALITY_FIELD_PREFIX);

/** Current value of a field item as the form edits it. */
function initialValue(item, lead) {
  if (item.key === 'nationality') return lead.nationality_id ? String(lead.nationality_id) : '';
  return String(lead[item.key] ?? '').slice(0, DATE_KEYS.includes(item.key) ? 10 : undefined);
}

/**
 * One popup for everything a verifier rejected on a lead: fix the fields and re-upload the
 * documents together. Items left untouched stay rejected. Saved items go back to the verifier
 * as "updated".
 *
 * @param items  rejected items: [{ type: 'field' | 'document', key, label, reason, remark, missing }]
 */
export default function RejectedItemUpdateModal({ leadId, items, onClose }) {
  const queryClient = useQueryClient();
  const open = !!items?.length;
  const fieldItems = (items || []).filter((i) => i.type === 'field' && !isNationalityField(i));
  const nationalityItems = (items || []).filter((i) => i.type === 'field' && isNationalityField(i));
  const documentItems = (items || []).filter((i) => i.type === 'document');

  const leadQuery = useQuery({ queryKey: ['leads', String(leadId)], queryFn: () => getLead(leadId), enabled: open });
  const nationalitiesQuery = useQuery({
    queryKey: ['nationalities'],
    queryFn: () => listNationalities(),
    enabled: open && (nationalityItems.length > 0 || (items || []).some((i) => i.key === 'nationality')),
  });
  const typesQuery = useQuery({ queryKey: ['document-types'], queryFn: listDocumentTypes, enabled: open && documentItems.length > 0 });

  const lead = leadQuery.data;
  const [values, setValues] = useState({});
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !lead) return;
    setError('');
    setFiles({});
    setAnswers(lead.nationality_fields || {});
    setValues(Object.fromEntries(fieldItems.map((i) => [i.key, initialValue(i, lead)])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lead, leadId]);

  const nationalityDef = (item) =>
    (nationalitiesQuery.data || [])
      .find((n) => n.id === lead?.nationality_id)
      ?.fields?.find((f) => `${NATIONALITY_FIELD_PREFIX}${f.id}` === item.key);

  const changedFields = fieldItems.filter((i) => values[i.key] !== undefined && values[i.key] !== initialValue(i, lead || {}));
  const changedAnswers = nationalityItems.length > 0 && JSON.stringify(answers) !== JSON.stringify(lead?.nationality_fields || {});
  const pickedFiles = documentItems.filter((i) => files[i.key]);
  const changes = changedFields.length + (changedAnswers ? 1 : 0) + pickedFiles.length;

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {};
      changedFields.forEach((i) => {
        const v = values[i.key];
        if (i.key === 'nationality') payload.nationality_id = v || null;
        else payload[i.key] = v === '' ? null : v;
      });
      if (changedAnswers) payload.nationality_fields = answers;
      if (Object.keys(payload).length) await updateLead(leadId, payload);

      for (const item of pickedFiles) {
        const type = typesQuery.data?.find((t) => t.code === item.key);
        if (!type) throw new Error(`Unknown document type for ${item.label}.`);
        // Replace, don't pile up: remove what was rejected before uploading the new file.
        for (const doc of (lead?.documents || []).filter((d) => d.document_type === item.key)) {
          await deleteLeadDocument(doc.id);
        }
        const formData = new FormData();
        formData.append('file', files[item.key]);
        formData.append('document_type_id', type.id);
        await uploadLeadDocument(leadId, formData);
      }
    },
    onSuccess: () => {
      ['leads', 'rejected-applicants'].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
      queryClient.invalidateQueries({ queryKey: ['lead-verification', leadId] });
      pushToast({ type: 'success', message: `${changes} item(s) updated — they will be re-checked at verification.` });
      onClose();
    },
    onError: (err) => {
      const errors = err.response?.data?.errors;
      setError((errors && Object.values(errors)[0]?.[0]) || err.response?.data?.message || err.message || 'Could not save.');
    },
  });

  const setValue = (key) => (v) => setValues((prev) => ({ ...prev, [key]: v }));

  const renderField = (item) => {
    const value = values[item.key] ?? '';
    if (item.key === 'nationality') {
      return (
        <Select label={item.label} value={value} onChange={(e) => setValue(item.key)(e.target.value)}>
          <option value="">Select…</option>
          {(nationalitiesQuery.data || []).map((n) => (
            <option key={n.id} value={n.id}>
              {n.name}
            </option>
          ))}
        </Select>
      );
    }
    if (PHONE_KEYS.includes(item.key)) return <PhoneInput label={item.label} value={value} onChange={setValue(item.key)} />;
    if (DATE_KEYS.includes(item.key)) return <DatePicker label={item.label} value={value} onChange={(e) => setValue(item.key)(e.target.value)} />;
    if (item.key === 'visa_type') {
      return (
        <Select label={item.label} value={value} onChange={(e) => setValue(item.key)(e.target.value)}>
          <option value="">Select…</option>
          <option value="visit">Visit</option>
          <option value="employment">Employment</option>
        </Select>
      );
    }
    return <Input label={item.label} value={value} onChange={(e) => setValue(item.key)(e.target.value)} />;
  };

  const renderNationalityField = (item) => {
    const def = nationalityDef(item);
    return def ? (
      <FieldAnswerList fields={[def]} answers={answers} onAnswer={(id, v) => setAnswers((a) => ({ ...a, [id]: v }))} />
    ) : (
      <p className="text-sm text-gray-400">Loading…</p>
    );
  };

  const renderDocument = (item) => (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700">{item.label}</label>
      <input
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp"
        onChange={(e) => setFiles((prev) => ({ ...prev, [item.key]: e.target.files[0] || null }))}
        className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary"
      />
      <p className="mt-1 text-xs text-gray-400">{item.missing ? 'Upload the missing document.' : 'Uploading replaces the rejected document.'}</p>
    </div>
  );

  const block = (item, body) => (
    <div key={`${item.type}:${item.key}`} className="rounded-md border border-gray-200 p-3">
      <div className="mb-2 rounded bg-red-50 px-2 py-1 text-xs text-red-800">
        Rejected: <span className="font-medium">{REASON_LABELS[item.reason] || item.reason}</span>
        {item.remark && <> — “{item.remark}”</>}
      </div>
      {body}
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Update rejected details"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={changes === 0 || !lead} loading={mutation.isPending} onClick={() => mutation.mutate()}>
            Save {changes > 0 ? `${changes} update${changes === 1 ? '' : 's'}` : 'updates'}
          </Button>
        </>
      }
    >
      {!lead ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : (
        <div className="space-y-3">
          {fieldItems.length + nationalityItems.length > 0 && (
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Fields</h4>
          )}
          {fieldItems.map((item) => block(item, renderField(item)))}
          {nationalityItems.map((item) => block(item, renderNationalityField(item)))}
          {documentItems.length > 0 && <h4 className="pt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Documents</h4>}
          {documentItems.map((item) => block(item, renderDocument(item)))}
          <p className="text-xs text-gray-500">Anything you leave unchanged stays rejected.</p>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}
    </Modal>
  );
}
